import { AI_JOB_SERVER_STATUSES, isAiJobServerStatus, type AdvancedReviewResult, type AiJobServerStatus, type AiJobSubject, type AiJobTier, type AiJobUsage, type AiJobWire } from '../../supabase/functions/_shared/aiJobsProtocol';
import { newId } from './ids';
import { V2_SCHEMA_VERSION } from './inspection';
import type { NonClientTestContextMarker } from './inspectionContext';

// The device's own record of one ADVANCED AI review job (Owner contract 2026-09-13 §6, §12, §43;
// Final Owner Decision 2026-09-14 CA-9 / CA-10 / D-CA-6). It mirrors the server's job (the server is
// the authority for status and result) and adds what only the device knows: which photos were sent,
// what was asked, and whether the result was viewed here.
//
// A job is ABOUT ONE CHECKLIST ROW: the deep review (the first job) reads the row's linked photos, its
// guidance, the AI observations and the inspector's note; every later question the inspector asks is
// a follow-up job that points at the one before it (`followUpOf`) — the row's advanced AI conversation.
// Like AI observations it lives OUTSIDE the inspector's official state (LAYER 1): nothing here ever
// becomes a result, a note or a report sentence unless the inspector writes it into the note.
//
// Local-only statuses come before the server accepted the job:
//   pendingUpload   the record exists, the photos are being prepared
//   submitting      the request is on its way (or its answer was lost — the next sync settles it)
//   notSubmitted    the server never accepted it (offline, refused, unreachable); retryable
export type AiJobLocalStatus = AiJobServerStatus | 'pendingUpload' | 'submitting' | 'notSubmitted';

export const AI_JOB_LOCAL_STATUSES: readonly AiJobLocalStatus[] = Object.freeze([...AI_JOB_SERVER_STATUSES, 'pendingUpload', 'submitting', 'notSubmitted']);

export function isAiJobLocalStatus(value: unknown): value is AiJobLocalStatus {
  return isAiJobServerStatus(value) || value === 'pendingUpload' || value === 'submitting' || value === 'notSubmitted';
}

export interface V2AiJob {
  schemaVersion: number;
  // Minted on the device; the server's job id and the idempotency key are this same value.
  id: string;
  tier: AiJobTier;
  inspectionId: string;
  areaId: string;
  // The checklist row this review is about. Every job of the new contract carries one.
  itemId: string;
  photoIds: string[];
  // The first job: the inspector's note on the row at submission. A follow-up: the question asked.
  inspectorNote: string;
  subject: AiJobSubject;
  // The completed job this one follows up on (one more question about the same row), or null.
  followUpOf: string | null;
  status: AiJobLocalStatus;
  attempt: number;
  errorCode: string | null;
  retryable: boolean;
  result: AdvancedReviewResult | null;
  usage: AiJobUsage | null;
  contextMarker: NonClientTestContextMarker | null;
  createdAt: number;
  updatedAt: number;
  submittedAt: number | null;
  completedAt: number | null;
  // When the inspector opened the result on THIS device (§43: a delivered push is not "viewed").
  viewedAt: number | null;
  lastSyncAt: number | null;
}

export interface NewAiJobInput {
  inspectionId: string;
  areaId: string;
  itemId: string;
  photoIds: readonly string[];
  inspectorNote: string;
  subject: AiJobSubject;
  contextMarker?: NonClientTestContextMarker | null;
  followUpOf?: string | null;
  id?: string;
}

export function newAiJobId(): string {
  return newId('aijob');
}

export function createAiJob(input: NewAiJobInput, now: number): V2AiJob {
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: input.id ?? newAiJobId(),
    tier: 'advanced',
    inspectionId: input.inspectionId,
    areaId: input.areaId,
    itemId: input.itemId,
    photoIds: [...new Set(input.photoIds)],
    inspectorNote: input.inspectorNote,
    subject: { ...input.subject },
    followUpOf: input.followUpOf ?? null,
    status: 'pendingUpload',
    attempt: 0,
    errorCode: null,
    retryable: false,
    result: null,
    usage: null,
    contextMarker: input.contextMarker ?? null,
    createdAt: now,
    updatedAt: now,
    submittedAt: null,
    completedAt: null,
    viewedAt: null,
    lastSyncAt: null
  };
}

export function withJobLocalStatus(job: V2AiJob, status: AiJobLocalStatus, now: number, errorCode: string | null = null, retryable: boolean = status === 'notSubmitted'): V2AiJob {
  return { ...job, status, errorCode, retryable, updatedAt: now };
}

// The server's truth about the job, merged over the device's record. Status, attempt, error,
// result, usage and completion come from the server; the device keeps its own viewed mark.
export function withJobServerState(job: V2AiJob, wire: AiJobWire, now: number): V2AiJob {
  return {
    ...job,
    status: wire.status,
    attempt: wire.attempt,
    errorCode: wire.errorCode,
    retryable: wire.retryable,
    result: wire.result ?? job.result,
    usage: wire.usage ?? job.usage,
    submittedAt: job.submittedAt ?? now,
    completedAt: wire.completedAt ?? (wire.status === 'completed' ? job.completedAt ?? now : job.completedAt),
    viewedAt: job.viewedAt ?? wire.viewedAt,
    lastSyncAt: now,
    updatedAt: now
  };
}

export function withJobViewed(job: V2AiJob, now: number): V2AiJob {
  return job.viewedAt !== null ? job : { ...job, viewedAt: now, updatedAt: now };
}

// The four groups of the AI Task Center (§12).
export type AiJobGroup = 'active' | 'unread' | 'viewed' | 'failed';

export const AI_JOB_GROUP_ORDER: readonly AiJobGroup[] = Object.freeze(['active', 'unread', 'viewed', 'failed']);

export function isAiJobActive(job: Pick<V2AiJob, 'status'>): boolean {
  return job.status === 'pendingUpload' || job.status === 'submitting' || job.status === 'queued' || job.status === 'processing' || job.status === 'retrying';
}

export function aiJobGroupOf(job: Pick<V2AiJob, 'status' | 'viewedAt'>): AiJobGroup {
  if (isAiJobActive(job)) return 'active';
  if (job.status === 'completed') return job.viewedAt === null ? 'unread' : 'viewed';
  return 'failed';
}

// Whether the device should ask the server about this job on the next sync: anything the server
// may still change, plus a submission whose answer was lost.
export function aiJobNeedsSync(job: Pick<V2AiJob, 'status'>): boolean {
  return job.status === 'submitting' || job.status === 'queued' || job.status === 'processing' || job.status === 'retrying';
}

// ---------- the row's advanced AI thread (CA-10 / D-CA-6) ----------

// One turn of the conversation as the sheet shows it: the inspector's question (a follow-up's note)
// and the AI's answer (the job's result). The first job is the deep review itself (no question).
export interface AiJobTurn {
  job: V2AiJob;
  question: string | null;
}

// The deep review of a row and every follow-up chained to it, oldest first. The root is the newest
// job of the row that is NOT a follow-up (a second deep review starts a new thread).
export function advancedThreadForItem(jobs: readonly V2AiJob[], itemId: string): AiJobTurn[] {
  const ofRow = jobs.filter((j) => j.itemId === itemId);
  const root = ofRow.filter((j) => j.followUpOf === null).sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
  if (!root) return [];
  const turns: AiJobTurn[] = [{ job: root, question: null }];
  const byParent = new Map<string, V2AiJob[]>();
  for (const job of ofRow) {
    if (!job.followUpOf) continue;
    const list = byParent.get(job.followUpOf) ?? [];
    list.push(job);
    byParent.set(job.followUpOf, list);
  }
  let current = root;
  for (;;) {
    const next = (byParent.get(current.id) ?? []).sort((a, b) => a.createdAt - b.createdAt)[0];
    if (!next) break;
    turns.push({ job: next, question: next.inspectorNote });
    current = next;
  }
  return turns;
}

// The latest completed review of the row (the root of its thread), or null.
export function latestReviewForItem(jobs: readonly V2AiJob[], itemId: string): V2AiJob | null {
  const thread = advancedThreadForItem(jobs, itemId);
  return thread.length > 0 ? thread[0].job : null;
}

// The last job of the thread — what a new question follows up on. null until the thread's last
// job has completed (a question is never asked of an unanswered one).
export function lastAnsweredJobOfThread(thread: readonly AiJobTurn[]): V2AiJob | null {
  const last = thread[thread.length - 1]?.job ?? null;
  return last && last.status === 'completed' && last.result ? last : null;
}
